/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-28
 */

/**
 * Public invitation API service for token-based invitation responses.
 * These endpoints do not require authentication and are accessed via invitation tokens.
 */

import { API_BASE_URL, API_VERSION } from '../config/api';
import { getAuthHeaders } from '../utils/sessionAuth';

// Base API URL — driven by runtime hostname so test deploys hit the test API.
const getBaseUrl = (): string => `${API_BASE_URL}/api/${API_VERSION}`;

// API response interfaces
export interface InvitationDetail {
  email: string;
  role: string;
  role_display: string;
  studio_name: string;
  studio_description: string;
  studio_logo?: string;
  invited_by_name: string;
  invitation_expires_at: string;
  expires_in_days: number;
  is_expired: boolean;
  is_valid: boolean;
}

export interface InvitationAcceptRequest {
  first_name?: string;
  last_name?: string;
  password?: string;
}

export interface InvitationDeclineRequest {
  feedback?: string;
}

export interface InvitationResponse {
  id: string;
  email: string;
  role: string;
  role_display: string;
  status: string;
  studio_name: string;
  user_name: string;
  activated_at: string;
}

export interface ApiError {
  detail?: string;
  [key: string]: any;
}

// API service class
class InvitationApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBaseUrl();
  }

  /**
   * Get invitation details by token
   */
  async getInvitationDetails(token: string): Promise<InvitationDetail> {
    const response = await fetch(`${this.baseUrl}/invitations/${token}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to fetch invitation details');
    }

    return response.json();
  }

  /**
   * Accept an invitation
   */
  async acceptInvitation(
    token: string,
    data: InvitationAcceptRequest
  ): Promise<InvitationResponse> {
    const response = await fetch(
      `${this.baseUrl}/invitations/${token}/accept/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // CSRF token for logged-in users (session auth enforces CSRF).
          ...getAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to accept invitation');
    }

    return response.json();
  }

  /**
   * Decline an invitation
   */
  async declineInvitation(
    token: string,
    data: InvitationDeclineRequest = {}
  ): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/invitations/${token}/decline/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // CSRF token for logged-in users (session auth enforces CSRF).
          ...getAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to decline invitation');
    }
  }
}

// Export singleton instance
export const invitationApi = new InvitationApiService();
export default invitationApi;
